import { useState, useEffect, useRef } from 'react';
import { Usd } from '@/utils/CurrencyContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { uploadImageToS3, checkS3Status } from '@/utils/s3Upload';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Package, Edit, Trash2, Image, Upload, CheckCircle, Clock, XCircle, Loader2, Cloud } from 'lucide-react';
import { motion } from 'framer-motion';
import { getUser } from '@/utils/auth';

const TeacherProductsPage = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [storageConfigured, setStorageConfigured] = useState(false);
  const fileInputRef = useRef(null);
  const createImageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: 'books',
    stock: '',
    is_digital: false,
    download_url: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    // Check R2/S3 storage status
    checkS3Status().then(status => {
      setStorageConfigured(status.configured);
    });
  }, []);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/marketplace/my-products');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load your products');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/marketplace/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: '',
      category: 'books',
      stock: '',
      is_digital: false,
      download_url: ''
    });
    setEditingProduct(null);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      setSelectedImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      stock: product.stock.toString(),
      is_digital: product.is_digital,
      download_url: product.download_url || ''
    });
    setShowCreateDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.price || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setImageUploadProgress(0);
    
    try {
      let imageUrl = null;
      
      // Upload image to R2 first if selected
      if (selectedImage && storageConfigured) {
        try {
          toast.info('Uploading image to cloud storage...');
          const uploadResult = await uploadImageToS3(
            selectedImage, 
            'products/',
            (progress) => setImageUploadProgress(progress)
          );
          imageUrl = uploadResult.url;
        } catch (imgError) {
          console.error('R2 upload failed:', imgError);
          toast.warning('Image upload failed. Continuing without image.');
        }
      }
      
      const payload = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        stock: parseInt(formData.stock) || 0,
        is_digital: formData.is_digital,
        download_url: formData.is_digital ? formData.download_url : null,
        images: imageUrl ? [imageUrl] : (editingProduct?.images || [])
      };

      let productId;
      
      if (editingProduct) {
        await api.put(`/marketplace/products/${editingProduct.id}`, payload);
        productId = editingProduct.id;
        toast.success('Product updated! Pending re-approval.');
      } else {
        const response = await api.post('/marketplace/products', payload);
        productId = response.data.product?.id;
        toast.success('Product created! Pending admin approval.');
      }

      // Fallback: If R2 wasn't configured and we have an image, try local upload
      if (selectedImage && !imageUrl && productId && !storageConfigured) {
        const imageFormData = new FormData();
        imageFormData.append('file', selectedImage);
        try {
          await api.post(`/marketplace/products/${productId}/images`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Image uploaded successfully!');
        } catch (imgError) {
          console.error('Image upload failed:', imgError);
          toast.error('Product created but image upload failed. You can add it later.');
        }
      }

      setShowCreateDialog(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setIsSubmitting(false);
      setImageUploadProgress(0);
    }
  };

  const handleImageUpload = async (productId, file) => {
    setUploadingImage(productId);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/marketplace/products/${productId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded!');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };

  const deleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    try {
      await api.delete(`/marketplace/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const getStatusBadge = (product) => {
    if (product.is_approved) {
      return (
        <Badge className="bg-green-100 text-green-700">
          <CheckCircle className="h-3 w-3 mr-1" />
          Approved
        </Badge>
      );
    }
    return (
      <Badge className="bg-amber-100 text-amber-700">
        <Clock className="h-3 w-3 mr-1" />
        Pending Approval
      </Badge>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(user?.role === 'seller' ? '/seller/dashboard' : '/teacher/dashboard')}
              className="mb-2 -ml-2"
              style={{ color: '#5C635A' }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1
              className="text-3xl font-bold tracking-tight"
              style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
            >
              My Products
            </h1>
            <p className="mt-2" style={{ color: '#5C635A' }}>
              Manage your marketplace product listings
            </p>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                className="rounded-full"
                style={{ backgroundColor: '#163329' }}
                data-testid="add-product-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                  {editingProduct ? 'Edit Product' : 'Create New Product'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div>
                  <Label htmlFor="title">Product Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., Beginner's Hair Styling Kit"
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Describe your product..."
                    className="mt-1 min-h-[100px]"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Price (USD) *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      placeholder="29.99"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({...formData, category: value})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: '#F5F5F0' }}>
                  <div>
                    <Label htmlFor="is_digital" className="font-medium">Digital Product</Label>
                    <p className="text-sm" style={{ color: '#5C635A' }}>
                      Enable for downloadable products
                    </p>
                  </div>
                  <Switch
                    id="is_digital"
                    checked={formData.is_digital}
                    onCheckedChange={(checked) => setFormData({...formData, is_digital: checked})}
                  />
                </div>

                {formData.is_digital ? (
                  <div>
                    <Label htmlFor="download_url">Download URL *</Label>
                    <Input
                      id="download_url"
                      type="url"
                      value={formData.download_url}
                      onChange={(e) => setFormData({...formData, download_url: e.target.value})}
                      placeholder="https://example.com/file.pdf"
                      className="mt-1"
                    />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="stock">Stock Quantity *</Label>
                    <Input
                      id="stock"
                      type="number"
                      min="0"
                      value={formData.stock}
                      onChange={(e) => setFormData({...formData, stock: e.target.value})}
                      placeholder="10"
                      className="mt-1"
                      required={!formData.is_digital}
                    />
                  </div>
                )}

                {/* Product Image Upload */}
                <div>
                  <Label>Product Image</Label>
                  <div 
                    className="mt-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-green-500 transition-colors"
                    style={{ borderColor: imagePreview ? '#163329' : '#E3E1DB' }}
                    onClick={() => createImageInputRef.current?.click()}
                  >
                    <input
                      ref={createImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    {imagePreview ? (
                      <div className="space-y-2">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-32 h-32 object-cover mx-auto rounded-lg"
                        />
                        <p className="text-sm" style={{ color: '#163329' }}>
                          Click to change image
                        </p>
                      </div>
                    ) : (
                      <div className="py-4">
                        <Upload className="h-10 w-10 mx-auto mb-2" style={{ color: '#5C635A' }} />
                        <p className="text-sm font-medium" style={{ color: '#163329' }}>
                          Click to upload product image
                        </p>
                        <p className="text-xs mt-1" style={{ color: '#5C635A' }}>
                          PNG, JPG up to 5MB
                        </p>
                      </div>
                    )}
                  </div>
                  {editingProduct && !imagePreview && editingProduct.images?.length > 0 && (
                    <p className="text-xs mt-2" style={{ color: '#5C635A' }}>
                      Current image will be kept if no new image is selected
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowCreateDialog(false);
                      resetForm();
                    }}
                    className="flex-1 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 rounded-full"
                    style={{ backgroundColor: '#163329' }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : editingProduct ? (
                      'Update Product'
                    ) : (
                      'Create Product'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Products List */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-32 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : products.length === 0 ? (
          <Card className="text-center py-16" style={{ borderColor: '#E3E1DB' }}>
            <CardContent>
              <Package className="h-16 w-16 mx-auto mb-4" style={{ color: '#E3E1DB' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#163329' }}>No products yet</h3>
              <p className="mb-6" style={{ color: '#5C635A' }}>
                Start selling by adding your first product to the marketplace.
              </p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="rounded-full"
                style={{ backgroundColor: '#163329' }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Product
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {products.map((product, idx) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card style={{ borderColor: '#E3E1DB' }}>
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 relative group">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={product.images[0]}
                            alt={product.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-8 w-8" style={{ color: '#E3E1DB' }} />
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={fileInputRef}
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleImageUpload(product.id, e.target.files[0]);
                            }
                          }}
                        />
                        <button
                          onClick={() => {
                            fileInputRef.current.click();
                          }}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                          {uploadingImage === product.id ? (
                            <Loader2 className="h-6 w-6 text-white animate-spin" />
                          ) : (
                            <Upload className="h-6 w-6 text-white" />
                          )}
                        </button>
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-semibold line-clamp-1" style={{ color: '#163329' }}>
                            {product.title}
                          </h3>
                          {getStatusBadge(product)}
                        </div>
                        <p className="text-sm line-clamp-2 mb-2" style={{ color: '#5C635A' }}>
                          {product.description}
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="font-bold" style={{ color: '#163329' }}>
                            <Usd value={product.price} />
                          </span>
                          <Badge variant="outline" className="capitalize">
                            {product.category}
                          </Badge>
                          {!product.is_digital && (
                            <span style={{ color: '#5C635A' }}>
                              Stock: {product.stock}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-4 pt-4 border-t" style={{ borderColor: '#E3E1DB' }}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(product)}
                        className="flex-1 rounded-full"
                        style={{ borderColor: '#163329', color: '#163329' }}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteProduct(product.id)}
                        className="rounded-full text-red-500 border-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherProductsPage;
